import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';

import { takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { BaseNestedFieldDefinition } from '../../../../core/field-definitions/entities/field-definition.entity';
import { Operand } from '../../../../core/query/entities/operand.entity';

import { ExtendedProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { AdvancedSearchRow } from '../../entities/advanced-search-row.entity';
import { AdvancedSearchDialogTranslations } from '../../entities/i18n/advanced-search-translations.entity';
import { ShAdvancedSearchRowContainerComponent } from '../advanced-search-row/advanced-search-row-container.component';

@Component({
    standalone: true,
    selector: 'sh-advanced-search-group',
    templateUrl: './advanced-search-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, ShAdvancedSearchRowContainerComponent]
})
export class ShAdvancedSearchGroupComponent extends BaseContainer implements OnInit {
    public formArray: UntypedFormArray;

    @Input() i18n!: AdvancedSearchDialogTranslations;
    @Input() filters: AdvancedSearchRow[];
    @Input() profileFields: ExtendedProfileField<BaseNestedFieldDefinition>[] = [];
    @Input() fieldDefinitionOptions: FormSelectOption[] = [];

    @Output() filtersChanged: EventEmitter<AdvancedSearchRow[]> = new EventEmitter<AdvancedSearchRow[]>();

    constructor(private readonly formBuilder: UntypedFormBuilder) {
        super();
    }

    ngOnInit(): void {
        this.initFormArray(this.filters);
        this.initFormArrayListener();
    }

    private initFormArray(filters: AdvancedSearchRow[]): void {
        this.formArray = this.formBuilder.array(this.filtersToFormGroups(filters));
    }

    private filtersToFormGroups(filters: AdvancedSearchRow[]): UntypedFormGroup[] {
        return filters.map(filter => this.createNewFormGroup(filter));
    }

    private createNewFormGroup(filter: AdvancedSearchRow): UntypedFormGroup {
        return this.formBuilder.group({
            Field: filter.Field,
            Value: filter.Value,
            Operand: filter.Operand
        });
    }

    private initFormArrayListener(): void {
        this.formArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(values => {
            this.filtersChanged.emit(values);
        });
    }

    public addControl(index: number): void {
        this.formArray.insert(index + 1, this.createNewField());
    }

    public removeControl(index: number): void {
        this.formArray.removeAt(index);
    }

    private createNewField(): UntypedFormGroup {
        return this.formBuilder.group({
            Operand: this.formBuilder.control(Operand.Include, [Validators.required]),
            Field: null,
            Value: null
        });
    }

    get entriesControls(): AbstractControl[] {
        return this.formArray.controls;
    }
}
